import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { AppCalendar } from '@/components/calendar/app-calendar';
import type { CalendarEvent, CalendarView } from '@/components/calendar/types';
import { RescheduleDialog } from '@/components/reschedule-dialog';
import { useLanguage } from '@/contexts/language-context';
import {
  updateJobVisit,
  type JobStatus,
  type JobVisitWithCrewDto,
  type PaymentMethod,
} from '@/lib/api/jobs';

import { visitTone } from '../jobs/helpers';
import { VisitRow } from './visit-row';

interface Props {
  visits: JobVisitWithCrewDto[];
  date: Date;
  view: CalendarView;
  onNavigate: (next: Date) => void;
  onView: (next: CalendarView) => void;
  businessId: string;
  jobId: string;
  jobTitle: string;
  jobDefaultPaymentMethod?: PaymentMethod | null;
  jobDefaultPaymentMethodId?: string | null;
}

function visitLabel(
  status: JobStatus,
  t: ReturnType<typeof useLanguage>['t'],
): string {
  if (status === 'started') return t.inProgress;
  if (status === 'completed') return t.completed;
  if (status === 'cancelled') return t.cancelled;
  return t.scheduled;
}

export function VisitCalendar({
  visits,
  date,
  view,
  onNavigate,
  onView,
  businessId,
  jobId,
  jobTitle,
  jobDefaultPaymentMethod,
  jobDefaultPaymentMethodId,
}: Props) {
  const { t } = useLanguage();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    id: string;
    label: string;
    from: string;
    to: string;
  } | null>(null);

  const reschedule = useMutation({
    mutationFn: ({
      visitId,
      date: scheduledDate,
    }: {
      visitId: string;
      date: string;
    }) =>
      updateJobVisit(businessId, jobId, visitId, {
        scheduled_date: scheduledDate,
      }),
    meta: { successMessage: t.visitRescheduled },
  });

  const events = useMemo<CalendarEvent[]>(
    () =>
      visits.map((visit) => ({
        id: visit.id,
        date: visit.scheduled_date,
        label: visitLabel(visit.status, t),
        tone: visitTone(visit.status),
        meta: visit,
      })),
    [visits, t],
  );

  const selected = visits.find((v) => v.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-3" data-testid="visit-calendar">
      <AppCalendar
        events={events}
        date={date}
        view={view}
        onNavigate={onNavigate}
        onView={onView}
        emptyLabel={t.calendarNoVisitsMonth}
        onSelectEvent={(event) => setSelectedId(event.id)}
        draggableAccessor={(event) => {
          const status = (event.meta as JobVisitWithCrewDto).status;
          return status !== 'completed' && status !== 'cancelled';
        }}
        onEventDrop={({ event, date: to }) =>
          setPending({ id: event.id, label: event.label, from: event.date, to })
        }
      />
      {selected && (
        <ul className="flex flex-col gap-2" data-testid="visit-calendar-selection">
          <VisitRow
            visit={selected}
            businessId={businessId}
            jobId={jobId}
            jobTitle={jobTitle}
            jobDefaultPaymentMethod={jobDefaultPaymentMethod}
            jobDefaultPaymentMethodId={jobDefaultPaymentMethodId}
          />
        </ul>
      )}
      {pending && (
        <RescheduleDialog
          title={pending.label}
          currentDate={pending.from}
          targetDate={pending.to}
          isPending={reschedule.isPending}
          onCancel={() => setPending(null)}
          onConfirm={(newISO) =>
            reschedule.mutate(
              { visitId: pending.id, date: newISO },
              { onSuccess: () => setPending(null) },
            )
          }
        />
      )}
    </div>
  );
}
